import {
  FAQ_BULK_MAX_TOPICS,
  type FaqTopic,
  faqTopicPrimaryLocale,
} from '@planuze/admin-contracts';
import {
  Button,
  Checkbox,
  EmptyState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { HelpCircle } from 'lucide-react';
import { useFaqAdminContent } from '../../content/sections/faq-admin.js';

interface Props {
  readonly items: readonly FaqTopic[];
  /** super_admin/support — habilita a coluna de Ações e o CTA de criar no empty. */
  readonly canManage: boolean;
  readonly onEdit: (topic: FaqTopic) => void;
  readonly onDelete: (topic: FaqTopic) => void;
  /** Remoção em flight — desabilita o botão Remover. */
  readonly deleting: boolean;
  readonly onCreate: () => void;
  /** Há busca ativa — diferencia "sem resultados" de "sem itens" (ADR-0700/0702/§6). */
  readonly hasFilters: boolean;
  readonly onClearFilters: () => void;
  readonly selectedSlugs?: ReadonlySet<string>;
  readonly onToggle?: (slug: string) => void;
  readonly onToggleAll?: (checked: boolean) => void;
}

/* Tabela POR-TÓPICO de FAQ/capacidades sobre o primitive `Table` de @planuze/ui (ADR-0700). Cada
   linha é um tópico (slug): rótulo da categoria + pergunta canônica (pt-BR) + os idiomas disponíveis
   como badges (verde = publicado, cinza = rascunho). A categoria exibida é o `categoryLabel` do
   idioma canônico (ADR-0720). A lista tem busca client-side (ADR-0702), então o empty diferencia
   "sem itens" (CTA de criar, só quem gerencia) de "sem resultados". */
export function FaqTable({
  items,
  canManage,
  onEdit,
  onDelete,
  deleting,
  onCreate,
  hasFilters,
  onClearFilters,
  selectedSlugs,
  onToggle,
  onToggleAll,
}: Props) {
  const copy = useFaqAdminContent().table;
  if (items.length === 0) {
    return hasFilters ? (
      <EmptyState
        icon={<HelpCircle className="size-6" />}
        title={copy.emptyFilteredTitle}
        description={copy.emptyFilteredDescription}
        primaryAction={
          <Button variant="outline" onClick={onClearFilters}>
            {copy.clearFilters}
          </Button>
        }
      />
    ) : (
      <EmptyState
        icon={<HelpCircle className="size-6" />}
        title={copy.emptyTitle}
        description={copy.emptyDescription}
        primaryAction={
          canManage ? <Button onClick={onCreate}>{copy.createButton}</Button> : undefined
        }
      />
    );
  }
  const selection = selectedSlugs ?? new Set<string>();
  const bulkEnabled = canManage && selectedSlugs !== undefined;
  const selectedVisibleCount = items.filter((topic) => selection.has(topic.slug)).length;
  const selectionTarget = Math.min(items.length, FAQ_BULK_MAX_TOPICS);
  const allSelected = selectionTarget > 0 && selectedVisibleCount >= selectionTarget;
  const selectionFull = selectedVisibleCount >= FAQ_BULK_MAX_TOPICS;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {bulkEnabled && (
            <TableHead className="w-10">
              <Checkbox
                checked={allSelected}
                onCheckedChange={(checked) => onToggleAll?.(checked === true)}
                aria-label={copy.selectPageAriaLabel}
              />
            </TableHead>
          )}
          <TableHead>{copy.columnCategory}</TableHead>
          <TableHead>{copy.columnQuestion}</TableHead>
          <TableHead>{copy.columnLanguages}</TableHead>
          <TableHead>{copy.columnOrder}</TableHead>
          {canManage && <TableHead>{copy.columnActions}</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((topic) => {
          const primary = faqTopicPrimaryLocale(topic);
          return (
            <TableRow key={topic.slug}>
              {bulkEnabled && (
                <TableCell>
                  <Checkbox
                    checked={selection.has(topic.slug)}
                    disabled={selectionFull && !selection.has(topic.slug)}
                    onCheckedChange={() => onToggle?.(topic.slug)}
                    aria-label={copy.selectRowAriaLabel(primary.question)}
                  />
                </TableCell>
              )}
              <TableCell>{primary.categoryLabel}</TableCell>
              <TableCell>{primary.question}</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {topic.locales.map((l) => (
                    <StatusBadge
                      key={l.locale}
                      variant={l.status === 'published' ? 'success' : 'neutral'}
                      title={l.status === 'published' ? copy.published : copy.draft}
                    >
                      {l.locale}
                    </StatusBadge>
                  ))}
                </div>
              </TableCell>
              <TableCell numeric className="text-muted-foreground">
                {topic.sortOrder}
              </TableCell>
              {canManage && (
                <TableCell>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => onEdit(topic)}>
                      {copy.edit}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(topic)}
                      disabled={deleting}
                    >
                      {copy.remove}
                    </Button>
                  </div>
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
